import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/MasterManagement.css';

const MasterManagement = ({ type }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Configuration for different types
  const config = {
    courses: {
      title: 'Manage Courses',
      apiEndpoint: '/master/courses.php',
      fields: [{ name: 'course_name', label: 'Course Name', type: 'text', required: true }],
      searchPlaceholder: 'Search courses...'
    },
    subjects: {
      title: 'Manage Subjects',
      apiEndpoint: '/master/subjects.php',
      fields: [{ name: 'subject_name', label: 'Subject Name', type: 'text', required: true }],
      searchPlaceholder: 'Search subjects...'
    },
    batches: {
      title: 'Manage Batches',
      apiEndpoint: '/master/batches.php',
      fields: [{ name: 'batch_name', label: 'Batch Time', type: 'text', required: true }],
      searchPlaceholder: 'Search batches...'
    },
    fees: {
      title: 'Enrolment Fees',
      apiEndpoint: '/master/fees.php',
      fields: [{ name: 'fee_name', label: 'Fee Name', type: 'text', required: true }],
      searchPlaceholder: 'Search fees...'
    },
    taxes: {
      title: 'Manage Tax',
      apiEndpoint: '/master/taxes.php',
      fields: [
        { name: 'tax_name', label: 'Tax Name', type: 'text', required: true },
        { name: 'tax_percentage', label: 'Tax Percentage (%)', type: 'number', required: true, min: 0, max: 100, step: 0.01 }
      ],
      searchPlaceholder: 'Search taxes...'
    }
  };

  const currentConfig = config[type];

  useEffect(() => {
    fetchItems();
  }, [type]);

  useEffect(() => {
    filterItems();
  }, [searchTerm, items]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showAddModal || showEditModal) {
          setShowAddModal(false);
          setShowEditModal(false);
        } else {
          navigate('/admin/dashboard');
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [navigate, showAddModal, showEditModal]);

  const fetchItems = async () => {
    try {
      const response = await api.get(currentConfig.apiEndpoint);
      if (response.data.success) {
        setItems(response.data.data);
        setFilteredItems(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    if (!searchTerm) {
      setFilteredItems(items);
      return;
    }

    const filtered = items.filter(item => {
      const searchString = Object.values(item).join(' ').toLowerCase();
      return searchString.includes(searchTerm.toLowerCase());
    });
    setFilteredItems(filtered);
  };

  const handleAdd = () => {
    const initialData = {};
    currentConfig.fields.forEach(field => {
      initialData[field.name] = '';
    });
    setFormData(initialData);
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setCurrentItem(item);
    setFormData(item);
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const response = await api.post(currentConfig.apiEndpoint, {
        action: 'delete',
        id: id
      });

      if (response.data.success) {
        alert('Deleted successfully!');
        fetchItems();
      }
    } catch (error) {
      alert('Failed to delete item');
    }
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(currentConfig.apiEndpoint, {
        action: 'add',
        ...formData
      });

      if (response.data.success) {
        alert('Added successfully!');
        setShowAddModal(false);
        fetchItems();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add item');
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(currentConfig.apiEndpoint, {
        action: 'update',
        ...formData
      });

      if (response.data.success) {
        alert('Updated successfully!');
        setShowEditModal(false);
        fetchItems();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update item');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="master-management-page">
      <div className="master-management-container">
        {/* Header */}
        <div className="master-header">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate('/admin/dashboard')} title="Back (ESC)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2>{currentConfig.title}</h2>
              <p>Manage your {currentConfig.title.toLowerCase()}</p>
            </div>
          </div>
          <button className="btn-add" onClick={handleAdd}>
            ➕ Add New
          </button>
        </div>

        {/* Search */}
        <div className="master-search">
          <input
            type="text"
            placeholder={currentConfig.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="master-table-container">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="no-data">No items found</div>
          ) : (
            <table className="master-table">
              <thead>
                <tr>
                  <th>ID</th>
                  {currentConfig.fields.map(field => (
                    <th key={field.name}>{field.label}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    {currentConfig.fields.map(field => (
                      <td key={field.name}>
                        {field.type === 'number' && field.name.includes('percentage') 
                          ? `${item[field.name]}%` 
                          : item[field.name]}
                      </td>
                    ))}
                    <td className="action-buttons">
                      <button className="btn-edit" onClick={() => handleEdit(item)} title="Edit">
                        ✏️
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)} title="Delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Summary */}
        <div className="master-summary">
          <p>Total: <strong>{filteredItems.length}</strong> of {items.length}</p>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New {type === 'taxes' ? 'Tax' : type.slice(0, -1)}</h3>
              <button className="close-modal" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitAdd}>
              {currentConfig.fields.map(field => (
                <div className="form-group" key={field.name}>
                  <label>{field.label} {field.required && <span className="required">*</span>}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                </div>
              ))}
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {type === 'taxes' ? 'Tax' : type.slice(0, -1)}</h3>
              <button className="close-modal" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitEdit}>
              {currentConfig.fields.map(field => (
                <div className="form-group" key={field.name}>
                  <label>{field.label} {field.required && <span className="required">*</span>}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                  />
                </div>
              ))}
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterManagement;
